import React, { useState, useMemo, useCallback } from 'react';
import { Allocation } from '../../config';
import { formatCurrency, formatCurrencyCompact } from '../../../../utils/currency';
import { aggregateByEmployee, initialsOf } from './aggregate';
import { formatDbDate } from './dates';
import './time-allocations-tab.scss';

interface TimeAllocationsTabProps {
  allocations: Allocation[];
}

interface TypeAggregate {
  employee_type: string;
  hours: number;
  total_value: number;
}

const aggregateByType = (allocations: Allocation[]): TypeAggregate[] => {
  const map = new Map<string, TypeAggregate>();
  for (const a of allocations) {
    const key = a.employee_type ?? 'Other';
    const existing = map.get(key);
    if (existing) {
      existing.hours += a.hours;
      existing.total_value += a.total_value;
    } else {
      map.set(key, { employee_type: key, hours: a.hours, total_value: a.total_value });
    }
  }
  return Array.from(map.values()).sort((a, b) =>
    a.employee_type.localeCompare(b.employee_type),
  );
};

const TimeAllocationsTab: React.FC<TimeAllocationsTabProps> = ({ allocations }) => {
  const [selected, setSelected] = useState<Set<number>>(new Set());

  // Sort allocations newest-first for display; keep the original-index
  // mapping so selection-state keys stay stable as the user toggles rows.
  const sortedRows = useMemo(
    () =>
      allocations
        .map((a, originalIndex) => ({ a, originalIndex }))
        .sort((x, y) => (x.a.date < y.a.date ? 1 : x.a.date > y.a.date ? -1 : 0)),
    [allocations],
  );

  const overallTotals = useMemo(() => {
    let hours = 0;
    let amount = 0;
    for (const a of allocations) {
      hours += a.hours;
      amount += a.total_value;
    }
    return { hours, amount };
  }, [allocations]);

  const employeeAggregates = useMemo(
    () => aggregateByEmployee(allocations).sort((a, b) => b.hours - a.hours),
    [allocations],
  );
  const typeAggregates = useMemo(() => aggregateByType(allocations), [allocations]);

  const selectedAllocations = useMemo(
    () => allocations.filter((_a, i) => selected.has(i)),
    [allocations, selected],
  );

  const selectedTotals = useMemo(() => {
    let hours = 0;
    let amount = 0;
    for (const a of selectedAllocations) {
      hours += a.hours;
      amount += a.total_value;
    }
    return { hours, amount };
  }, [selectedAllocations]);

  const selectedEmployeeAggregates = useMemo(
    () => aggregateByEmployee(selectedAllocations).sort((a, b) => b.hours - a.hours),
    [selectedAllocations],
  );

  const toggleSelected = useCallback((index: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => setSelected(new Set()), []);

  if (allocations.length === 0) {
    return (
      <div className="job-card-tab">
        <div className="ui-table__empty">No time has been allocated to this job yet.</div>
      </div>
    );
  }

  return (
    <div className="job-card-tab time-allocations-tab">
      <div className="stat-grid">
        {typeAggregates.map((t) => (
          <div key={t.employee_type} className="stat">
            <span className="stat__value">{formatCurrencyCompact(t.total_value)}</span>
            <span className="stat__label">
              {t.employee_type} · {t.hours.toFixed(1)} hrs
            </span>
          </div>
        ))}
        <div className="stat time-allocations-tab__stat-total">
          <span className="stat__value">{formatCurrencyCompact(overallTotals.amount)}</span>
          <span className="stat__label">Combined · {overallTotals.hours.toFixed(1)} hrs</span>
        </div>
      </div>

      <div className="ui-cards time-allocations-tab__people">
        {employeeAggregates.map((emp) => (
          <div key={emp.employee_name} className="ui-card ui-card--static">
            <span className="ui-card__avatar" aria-hidden="true">
              {initialsOf(emp.employee_name)}
            </span>
            <div className="ui-card__main">
              <span className="ui-card__title">{emp.employee_name}</span>
              {emp.employee_type && <span className="ui-card__meta">{emp.employee_type}</span>}
            </div>
            <div className="ui-card__side time-allocations-tab__person-figures">
              <span>{emp.hours.toFixed(2)} hrs</span>
              <strong>{formatCurrency(emp.total_value)}</strong>
            </div>
          </div>
        ))}
      </div>

      <table className="ui-table">
        <thead>
          <tr>
            <th className="time-allocations-tab__col-select" aria-label="Select" />
            <th className="col-date">Date</th>
            <th>Employee</th>
            <th className="time-allocations-tab__col-description">Description</th>
            <th className="is-num">Hours</th>
            <th className="is-num">Rate</th>
            <th className="is-num">Total</th>
          </tr>
        </thead>
        <tbody>
          {sortedRows.map(({ a, originalIndex }) => {
            const isSelected = selected.has(originalIndex);
            return (
              <tr
                key={originalIndex}
                className={`is-clickable${isSelected ? ' is-editing' : ''}`}
                onClick={() => toggleSelected(originalIndex)}
              >
                <td className="time-allocations-tab__col-select">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleSelected(originalIndex)}
                    onClick={(e) => e.stopPropagation()}
                    aria-label={`Select ${a.employee_name}, ${formatDbDate(a.date)}`}
                  />
                </td>
                <td>{formatDbDate(a.date)}</td>
                <td>{a.employee_name}</td>
                <td className="time-allocations-tab__col-description">
                  {a.description || <span className="ui-table__na">—</span>}
                </td>
                <td className="is-num">{a.hours.toFixed(2)}</td>
                <td className="is-num">{formatCurrency(a.charge_rate)}</td>
                <td className="is-num">{formatCurrency(a.total_value)}</td>
              </tr>
            );
          })}
        </tbody>
        <tfoot className="ui-table__totals">
          <tr>
            {/* The description cell is its own <td> so it hides with its
                column on narrow screens; one colSpan across it left the
                totals a column out of step. */}
            <td colSpan={3}>Total</td>
            <td className="time-allocations-tab__col-description" />
            <td className="is-num">{overallTotals.hours.toFixed(2)}</td>
            <td />
            <td className="is-num">{formatCurrency(overallTotals.amount)}</td>
          </tr>
        </tfoot>
      </table>

      {selected.size > 0 && (
        <div className="time-allocations-tab__selection-footer">
          <div className="time-allocations-tab__selection-header">
            <span className="time-allocations-tab__selection-label">
              {selected.size} selected
            </span>
            <div className="time-allocations-tab__selection-totals">
              <span>{selectedTotals.hours.toFixed(2)} hrs</span>
              <span>{formatCurrency(selectedTotals.amount)}</span>
            </div>
            <button
              type="button"
              className="time-allocations-tab__selection-clear"
              onClick={clearSelection}
            >
              Clear
            </button>
          </div>
          {selectedEmployeeAggregates.length > 0 && (
            <div className="time-allocations-tab__selection-employees">
              {selectedEmployeeAggregates.map((emp) => (
                <div
                  key={emp.employee_name}
                  className="time-allocations-tab__selection-employee"
                >
                  <span>{emp.employee_name}</span>
                  <span>{emp.hours.toFixed(2)} hrs</span>
                  <span>{formatCurrency(emp.total_value)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TimeAllocationsTab;
